import { useState, useEffect, useCallback } from 'react';
import {
  CalendarOutlined,
  EditOutlined,
  PlusCircleOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { useToast } from '../../hooks/toast';
import {
  ATable,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
  StatusActive,
} from '../../components';
import HolidayCreateEdit from './CreateEdit';
import { IHoliday } from '../../shared/interfaces';
import { getHolidays } from '../../http';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const Holidays: React.FC = () => {
  const { addToast } = useToast();
  const { permissionFind } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [holiday, setHoliday] = useState<IHoliday | null>(null);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [holidays, setHolidays] = useState<IHoliday[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const list = useCallback(async () => {
    setLoading(true);
    setHolidays([]);
    try {
      const response = await getHolidays(limit, (page - 1) * limit, search);
      setPages(response.count);
      setHolidays(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar feriados',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  const renderModalHolidayCreateEdit = () => {
    if (modalCreate)
      return (
        <HolidayCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          holiday={holiday}
          setHolidays={setHolidays}
        />
      );
  };

  useEffect(() => {
    list();
  }, [page, search, limit, list]);

  return (
    <PageLayout>
      <PageIntro
        badge="Cadastros"
        icon={<CalendarOutlined />}
        title="Feriados"
        description="Gerencie os feriados usados no calendário da clínica com uma visualização mais clara e alinhada ao restante do sistema."
      />
      <MenuOptions
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => list(),
          },
          permissionFind([PermissionActions.holidaysCreate])
            ? {
                label: 'Cadastrar',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => {
                  setHoliday(null);
                  setModalCreate(true);
                },
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <ATable
          scroll={{ y: 510 }}
          columns={[
            {
              title: 'Id',
              render: (value, record, index) => <>{index + 1}</>,
            },
            {
              title: 'Descrição',
              dataIndex: 'description',
            },
            {
              title: 'Dia',
              dataIndex: 'day',
            },
            {
              title: 'Mês',
              dataIndex: 'month',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: 'Opções',
              width: 130,
              render: (cell: IHoliday) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.holidaysUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setHoliday(cell);
                            setModalCreate(true);
                          },
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={holidays}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {renderModalHolidayCreateEdit()}
    </PageLayout>
  );
};

export default Holidays;
